import { createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit'
import { injectedLocalApi } from '@/services/local'
import { savedCreditCardAdapter } from '@/redux/checkout/schema'
import { extendPaymentMethodsWithParsedIdField } from '@/utils/payment'

type PlainInfo<T> = {
  status: RequestState
  error?: string | null
  data: T
}

type RequestState = 'pending' | 'fulfilled' | 'rejected' | 'none'

interface CheckoutState {
  meta: PlainInfo<Oms.CheckoutMetaData>
  vouchers: PlainInfo<Oms.Vouchers[]>
  selectedVoucher: PlainInfo<Oms.SelectedVoucher>
  paymentMethods: PlainInfo<Oms.PaymentMethod[]>
  // all saved credit cards of customer
  savedCreditCards: {
    data: EntityState<User.SavedCreditCard, string>
  }
  // if not null, it means customer is selecting a saved credit card
  selectedSavedCreditCard: User.SavedCreditCard | null
  // current selecting payment method
  selectedPaymentMethod: Oms.PaymentMethod | null
  selectedApplePay: boolean
  showSavingCreditCard: boolean
  paymentProgress: PlainInfo<Oms.CheckoutMetaData>
}

const initialState: CheckoutState = {
  meta: {
    status: 'none',
    error: null,
    data: {} as Oms.CheckoutMetaData,
  },
  vouchers: {
    status: 'none',
    data: [] as Oms.Vouchers[],
  },
  selectedVoucher: {
    status: 'none',
    data: {} as Oms.SelectedVoucher,
  },
  paymentMethods: {
    status: 'none',
    data: [],
  },
  savedCreditCards: {
    data: savedCreditCardAdapter.getInitialState(),
  },
  selectedSavedCreditCard: null,
  selectedPaymentMethod: null,
  selectedApplePay: false,
  showSavingCreditCard: false,
  paymentProgress: {
    status: 'none',
    error: null,
    data: {} as Oms.CheckoutMetaData,
  },
}

export const checkoutSlice = createSlice({
  name: '@checkout',
  initialState,
  reducers: {
    setDefaultCheckoutOrder: (
      state,
      action: PayloadAction<Oms.V4ApiResponse<Oms.CheckoutMetaData>>
    ) => {
      state.meta.data = action.payload.data
      state.meta.error = action?.payload?.errors?.[0]?.errorCode
      state.meta.status = !action?.payload?.errors?.[0]?.errorCode
        ? 'fulfilled'
        : 'rejected'
      if (action.payload?.data?.storeOrder?.orderVouchers?.[0]) {
        state.selectedVoucher.status = 'fulfilled'
        state.selectedVoucher.data =
          action.payload.data.storeOrder.orderVouchers[0]
      }
      if (typeof window !== 'undefined' && state.meta.data?.storeOrder) {
        state.meta.data.storeOrder.cachedCheckoutAddress = JSON.parse(
          window.localStorage.getItem('cachedCheckoutAddress') || '{}'
        )
      }
    },
    clearSelectedVouchers: (state) => {
      state.selectedVoucher.status = 'none'
      state.selectedVoucher.data = {} as Oms.SelectedVoucher
    },
    selectSavedCreditCard: (
      state,
      action: PayloadAction<User.SavedCreditCard>
    ) => {
      state.selectedSavedCreditCard = action.payload
    },
    clearSelectedSavedCreditCard: (state) => {
      state.selectedSavedCreditCard = null
    },
    selectPaymentMethod: (state, action: PayloadAction<Oms.PaymentMethod>) => {
      state.selectedPaymentMethod = action.payload
    },
    selectApplePay: (state, action: PayloadAction<boolean>) => {
      state.selectedApplePay = action.payload
    },
    toggleShowHideSavingCreditCard: (state, action: PayloadAction<boolean>) => {
      state.showSavingCreditCard = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        injectedLocalApi.endpoints.updateCheckoutMeta.matchFulfilled,
        (state, action) => {
          state.meta.data.storeOrder.recipientContact =
            action.payload.data.data.recipientContact
        }
      )
      .addMatcher(
        injectedLocalApi.endpoints.getPaymentOptions.matchFulfilled,
        (state, action) => {
          state.paymentMethods.status = 'fulfilled'
          const filteredPaymentMethods = extendPaymentMethodsWithParsedIdField(
            action.payload.data.payload.paymentOptions.filter(
              (paymentMethod) => paymentMethod.enableOnCheckout
            )
          )
          state.paymentMethods.data = filteredPaymentMethods

          let initializeSelectedPaymentMethod

          if (
            filteredPaymentMethods.length > 0 &&
            action.meta.arg.originalArgs.enabledStripePaymentElement
          ) {
            initializeSelectedPaymentMethod = filteredPaymentMethods.find(
              (paymentMethod) => [7, 20].includes(paymentMethod.type)
            )
          } else if (filteredPaymentMethods.length > 0) {
            initializeSelectedPaymentMethod = filteredPaymentMethods[0]
          }

          if (initializeSelectedPaymentMethod) {
            state.selectedPaymentMethod = initializeSelectedPaymentMethod
          }
        }
      )
      .addMatcher(
        injectedLocalApi.endpoints.getCheckoutMeta.matchFulfilled,
        (state, action) => {
          state.meta.status = 'fulfilled'
          state.meta.data = {
            ...action.payload.data.data,
            storeOrder: {
              ...action.payload.data.data.storeOrder,
              ...(typeof window !== 'undefined'
                ? {
                    cachedCheckoutAddress: JSON.parse(
                      window.localStorage.getItem('cachedCheckoutAddress') ||
                        '{}'
                    ),
                  }
                : {}),
            },
          }
        }
      )
      .addMatcher(
        injectedLocalApi.endpoints.removeSelectedVoucher.matchFulfilled,
        (state, action) => {
          state.selectedVoucher.data = {} as Oms.SelectedVoucher
        }
      )
      .addMatcher(
        injectedLocalApi.endpoints.getVouchers.matchFulfilled,
        (state, action) => {
          state.vouchers.status = 'fulfilled'
          state.vouchers.data = action.payload.data.payload
        }
      )
      .addMatcher(
        injectedLocalApi.endpoints.validateVoucher.matchFulfilled,
        (state, action) => {
          if (action.payload.data) {
            state.selectedVoucher.data = action.payload.data
          }
        }
      )
      .addMatcher(
        injectedLocalApi.endpoints.getAllSavedCards.matchFulfilled,
        (state, action) => {
          if (action.payload.paymentMethods) {
            state.savedCreditCards.data = savedCreditCardAdapter.addMany(
              state.savedCreditCards.data,
              action.payload.paymentMethods
            )

            if (
              state.selectedPaymentMethod?.type &&
              [7, 20].includes(state.selectedPaymentMethod.type)
            ) {
              const defaultCard = action.payload.paymentMethods.find((card) => {
                if (action.payload.defaultPmId) {
                  return card.id === action.payload.defaultPmId
                }
                if (action.payload.paymentMethods[0]?.id) {
                  return card.id === action.payload.paymentMethods[0].id
                }
              })
              if (defaultCard) {
                state.selectedSavedCreditCard = defaultCard
              }
            }
          }
        }
      )
      .addMatcher(
        injectedLocalApi.endpoints.postStripe3DsCharges.matchPending,
        (state, action) => {
          state.paymentProgress.status = 'pending'
        }
      )
      .addMatcher(
        injectedLocalApi.endpoints.postStripe3DsCharges.matchFulfilled,
        (state, action) => {
          state.paymentProgress.status = 'fulfilled'
          state.paymentProgress.data = action.payload.data.data
          state.paymentProgress.error =
            action.payload.data.errors?.[0]?.errorCode
        }
      )
      .addMatcher(
        injectedLocalApi.endpoints.postStripe3DsCharges.matchRejected,
        (state, action) => {
          state.paymentProgress.status = 'rejected'
          state.paymentMethods.error = (
            action.payload?.data as any
          )?.errors?.[0]?.errorCode
        }
      )
      .addMatcher(
        injectedLocalApi.endpoints.updateDeliveryDetails.matchFulfilled,
        (state, action) => {
          state.meta.data = {
            ...state.meta.data,
            storeOrder: {
              ...(action.payload.data.data
                ? action.payload.data.data
                : state.meta.data.storeOrder),
              ...(typeof window !== 'undefined'
                ? {
                    cachedCheckoutAddress: JSON.parse(
                      window.localStorage.getItem('cachedCheckoutAddress') ||
                        '{}'
                    ),
                  }
                : {}),
            },
          }
        }
      )
  },
})

export const {
  setDefaultCheckoutOrder,
  clearSelectedVouchers,
  selectSavedCreditCard,
  clearSelectedSavedCreditCard,
  selectPaymentMethod,
  selectApplePay,
  toggleShowHideSavingCreditCard,
} = checkoutSlice.actions
